'use client'

import * as React from 'react'
import { type ComponentProps, useEffect } from 'react'
import { Button } from '@cocoonspace/ui/components/button'
import { cn } from '@cocoonspace/ui/lib/utils'
import useEmblaCarousel, {
	type UseEmblaCarouselType,
} from 'embla-carousel-react'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
	opts?: CarouselOptions
	plugins?: CarouselPlugin
	orientation?: 'horizontal' | 'vertical'
	setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0]
	api: ReturnType<typeof useEmblaCarousel>[1]
	scrollPrev: () => void
	scrollNext: () => void
	canScrollPrev: boolean
	canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

const useCarousel = () => {
	const context = React.useContext(CarouselContext)

	if (!context) {
		throw new Error('useCarousel must be used within a <Carousel />')
	}

	return context
}

const Carousel = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
	(
		{
			orientation = 'horizontal',
			opts,
			setApi,
			plugins,
			className,
			children,
			...props
		},
		ref,
	) => {
		const [carouselRef, api] = useEmblaCarousel(
			{
				...opts,
				axis: orientation === 'horizontal' ? 'x' : 'y',
			},
			plugins,
		)
		const [canScrollPrev, setCanScrollPrev] = React.useState(false)
		const [canScrollNext, setCanScrollNext] = React.useState(false)

		const onSelect = React.useCallback((api: CarouselApi) => {
			if (!api) {
				return
			}

			setCanScrollPrev(api.canScrollPrev())
			setCanScrollNext(api.canScrollNext())
		}, [])

		const scrollPrev = React.useCallback(() => {
			api?.scrollPrev()
		}, [api])

		const scrollNext = React.useCallback(() => {
			api?.scrollNext()
		}, [api])

		const handleKeyDown = React.useCallback(
			(event: React.KeyboardEvent<HTMLDivElement>) => {
				if (event.key === 'ArrowLeft') {
					event.preventDefault()
					scrollPrev()
				} else if (event.key === 'ArrowRight') {
					event.preventDefault()
					scrollNext()
				}
			},
			[scrollPrev, scrollNext],
		)

		React.useEffect(() => {
			if (!api || !setApi) return

			setApi(api)
		}, [api, setApi])

		React.useEffect(() => {
			if (!api) return

			onSelect(api)
			api.on('reInit', onSelect)
			api.on('select', onSelect)

			return () => {
				api?.off('select', onSelect)
			}
		}, [api, onSelect])

		return (
			<CarouselContext.Provider
				value={{
					api: api,
					canScrollNext,
					canScrollPrev,
					carouselRef,
					opts,
					orientation:
						orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
					scrollNext,
					scrollPrev,
				}}
			>
				<div
					aria-roledescription='carousel'
					className={cn('relative', className)}
					onKeyDownCapture={handleKeyDown}
					ref={ref}
					role='region'
					{...props}
				>
					{children}
				</div>
			</CarouselContext.Provider>
		)
	},
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	const { carouselRef, orientation } = useCarousel()

	return (
		<div
			className='overflow-hidden'
			ref={carouselRef}
		>
			<div
				className={cn(
					'flex',
					orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
					className,
				)}
				ref={ref}
				{...props}
			/>
		</div>
	)
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	const { orientation } = useCarousel()

	return (
		<div
			aria-roledescription='slide'
			className={cn(
				'min-w-0 shrink-0 grow-0 basis-full',
				orientation === 'horizontal' ? 'pl-4' : 'pt-4',
				className,
			)}
			ref={ref}
			role='group'
			{...props}
		/>
	)
})
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof Button>
>(({ className, variant = 'ghost', size = 'default', ...props }, ref) => {
	const { orientation, scrollPrev, canScrollPrev } = useCarousel()

	return (
		<Button
			className={cn(
				'absolute disabled:opacity-0',
				orientation === 'horizontal'
					? 'top-1/2 -left-12 -translate-y-1/2'
					: '-top-12 left-1/2 -translate-x-1/2 rotate-90',
				className,
			)}
			disabled={!canScrollPrev}
			icon
			onClick={(e) => {
				e.preventDefault()
				scrollPrev()
			}}
			ref={ref}
			size={size}
			variant={variant}
			{...props}
		>
			<ChevronLeftIcon className='size-6!' />
			<span className='sr-only'>Previous slide</span>
		</Button>
	)
})
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof Button>
>(({ className, variant = 'ghost', size = 'default', ...props }, ref) => {
	const { orientation, scrollNext, canScrollNext } = useCarousel()

	return (
		<Button
			className={cn(
				'absolute disabled:opacity-0',
				orientation === 'horizontal'
					? 'top-1/2 -right-12 -translate-y-1/2'
					: '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
				className,
			)}
			disabled={!canScrollNext}
			icon
			onClick={(e) => {
				e.preventDefault()
				scrollNext()
			}}
			ref={ref}
			size={size}
			variant={variant}
			{...props}
		>
			<ChevronRightIcon className='size-6!' />
			<span className='sr-only'>Next slide</span>
		</Button>
	)
})
CarouselNext.displayName = 'CarouselNext'

const CarouselIndicators = ({ className, ...props }: ComponentProps<'div'>) => {
	const { api } = useCarousel()
	const [current, setCurrent] = React.useState(0)
	const [count, setCount] = React.useState(0)

	useEffect(() => {
		if (!api) return

		const updateIndex = () => setCurrent(api.selectedScrollSnap() + 1)

		updateIndex()
		setCount(api.scrollSnapList().length)

		api.on('reInit', updateIndex)
		api.on('resize', updateIndex)
		api.on('select', updateIndex)
	}, [api])

	return (
		<div
			{...props}
			className={cn('flex w-full justify-center gap-2', className)}
		>
			{Array.from({ length: count }, (_, i) => (
				<button
					className='disabled:bg-primary h-3 w-3 rounded-full bg-white outline-none'
					disabled={current === i + 1}
					key={i}
					onClick={() => api?.scrollTo(i)}
					type='button'
				/>
			))}
		</div>
	)
}

CarouselIndicators.displayName = 'CarouselIndicators'

export {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselIndicators,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
}
